import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from './useAuth';
import { useWatchlistStore } from '../stores';
import { useCompanySummaryCache } from './useCompanySummaryCache';
import { useProgressiveCompanyDataSimple as useProgressiveCompanyData } from './useProgressiveCompanyDataSimple';
import { companiesAPI, industriesAPI, watchlistAPI } from '../lib/supabase';

export const useWatchlistManager = () => {
  const { user } = useAuth();
  const { preloadMultiple } = useCompanySummaryCache();
  const { transformToProgressiveData, loadCompanyMetrics } = useProgressiveCompanyData();
  
  const {
    watchedCompanies,
    watchedIndustries,
    allCompanies,
    allIndustries,
    watchlistInitialized,
    loading,
    setWatchedCompanies,
    setWatchedIndustries,
    setAllCompanies,
    setAllIndustries,
    setWatchlistInitialized,
    setLoading,
    resetSelections,
    resetAll,
  } = useWatchlistStore();

  const [dataLoaded, setDataLoaded] = useState(false);
  const loadingRef = useRef(false);
  
  // Load watchlist data using optimized API
  const loadWatchlistData = useCallback(async () => {
    if (!user || dataLoaded || loadingRef.current) return;
    
    loadingRef.current = true;
    
    setLoading(true);
    try {
      // 使用新的优化API，一次性获取所有数据
      const watchlistData = await watchlistAPI.getUserCompleteWatchlist(user.id);
      
      // 直接设置数据，无需前端筛选
      setAllCompanies(watchlistData.allCompanies);
      setAllIndustries(watchlistData.allIndustries);
      setWatchedCompanies(watchlistData.watchedCompanies);
      setWatchedIndustries(watchlistData.watchedIndustries);
      
      // Load company metrics asynchronously (如果需要更详细的数据)
      if (watchlistData.watchedCompanies.length > 0) {
        const companyIds = watchlistData.watchedCompanies.map(c => c.id);
        loadCompanyMetrics(companyIds);
      }
      
      // Preload top companies
      if (watchlistData.watchedCompanies.length > 0) {
        const topCompanyIds = watchlistData.watchedCompanies.slice(0, 3).map(c => c.id);
        preloadMultiple(topCompanyIds);
      }
      
      setDataLoaded(true);
      setWatchlistInitialized(true);
    } catch (error) {
      console.error('Error loading watchlist data:', error);
      setWatchlistInitialized(true); // Mark as initialized even on error
      
      // 如果新API失败，回退到旧的方式
      console.log('Falling back to legacy API calls...');
      try {
        const [companies, industries] = await Promise.all([
          companiesAPI.getCompanies(),
          industriesAPI.getIndustries(),
        ]);
        
        setAllCompanies(companies);
        setAllIndustries(industries);
        
        const watchlist = await watchlistAPI.getUserWatchlist(user.id);
        
        const watchedCompanyIds = watchlist
          .filter(item => item.item_type === 'company')
          .map(item => item.item_id);
        
        const watchedIndustryIds = watchlist
          .filter(item => item.item_type === 'industry')
          .map(item => item.item_id);
        
        const watchedCompanyList = companies.filter(company =>
          watchedCompanyIds.includes(company.id)
        );
        
        // Transform companies to include progressive data
        const companiesWithData = transformToProgressiveData(watchedCompanyList);
        setWatchedCompanies(companiesWithData);
        
        // Process industries with news count
        const industriesWithNews = industries
          .filter(industry => watchedIndustryIds.includes(industry.id))
          .map(industry => {
            let newsCount = 0;
            if (industry.summary_24hrs) {
              try {
                const summaryData = JSON.parse(industry.summary_24hrs);
                newsCount = summaryData.sources?.length || 0;
              } catch {
                newsCount = industry.current_article_count_24hrs || 0;
              }
            } else {
              newsCount = industry.current_article_count_24hrs || 0;
            }
            return { ...industry, newsCount };
          });
        
        setWatchedIndustries(industriesWithNews);
        
        setDataLoaded(true);
      } catch (fallbackError) {
        console.error('Fallback API also failed:', fallbackError);
      }
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, dataLoaded]);

  // Reset all states when user ID changes
  const prevUserIdRef = useRef<string | undefined>(undefined);
  const isFirstMountRef = useRef(true);
  
  useEffect(() => {
    const currentUserId = user?.id;
    const prevUserId = prevUserIdRef.current;
    
    // Skip on first mount
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      prevUserIdRef.current = currentUserId;
      return;
    }
    
    // Only reset when there's an actual user change
    if (prevUserId !== currentUserId) {
      // User switched
      if (currentUserId) {
        // User logged in or switched to different user - reset UI states only
        resetSelections();
        setDataLoaded(false);
        setWatchlistInitialized(false);
      } else {
        // User logged out - clear all data and UI states
        resetAll();
        setDataLoaded(false);
        setWatchlistInitialized(true); // Set to true for logged out state
      }
    }
    
    // Update the ref
    prevUserIdRef.current = currentUserId;
  }, [user, resetAll, resetSelections, setWatchlistInitialized]);
  
  // Load data when user changes
  useEffect(() => {
    if (user && !dataLoaded && !loadingRef.current) {
      loadWatchlistData();
    }
  }, [user, dataLoaded, loadWatchlistData]);
  
  // Refresh watchlist
  const refreshWatchlist = useCallback(async () => {
    setDataLoaded(false);
    await loadWatchlistData();
  }, [loadWatchlistData]);
  
  // Add company to watchlist
  const addCompanyToWatchlist = useCallback(async (companyName: string) => {
    if (!user) throw new Error('User not authenticated');
    
    // Find or create company
    let company = allCompanies.find(c => 
      c.name.toLowerCase() === companyName.toLowerCase()
    );
    
    if (!company) {
      company = await companiesAPI.addCompany(companyName);
      setAllCompanies(prev => [...prev, company!]);
    }
    
    // Add to watchlist
    await watchlistAPI.addToWatchlist(user.id, 'company', company.id);
    await refreshWatchlist();
  }, [user, allCompanies, refreshWatchlist, setAllCompanies]);
  
  // Remove company from watchlist
  const removeCompanyFromWatchlist = useCallback(async (companyId: string) => {
    if (!user) throw new Error('User not authenticated');
    
    await watchlistAPI.removeFromWatchlist(user.id, 'company', companyId);
    await refreshWatchlist();
  }, [user, refreshWatchlist]);
  
  // Add industry to watchlist
  const addIndustryToWatchlist = useCallback(async (name: string, description: string) => {
    if (!user) throw new Error('User not authenticated');
    
    // Find or create industry
    let industry = allIndustries.find(i => 
      i.name.toLowerCase() === name.toLowerCase()
    );
    
    if (!industry) {
      industry = await industriesAPI.addIndustry(name, description);
      setAllIndustries(prev => [...prev, industry!]);
    }
    
    // Add to watchlist
    await watchlistAPI.addToWatchlist(user.id, 'industry', industry.id);
    await refreshWatchlist();
  }, [user, allIndustries, refreshWatchlist, setAllIndustries]);
  
  // Remove industry from watchlist
  const removeIndustryFromWatchlist = useCallback(async (industryId: string) => {
    if (!user) throw new Error('User not authenticated');
    
    await watchlistAPI.removeFromWatchlist(user.id, 'industry', industryId);
    await refreshWatchlist();
  }, [user, refreshWatchlist]);

  return {
    // Data states
    watchedCompanies,
    watchedIndustries,
    allCompanies,
    allIndustries,
    watchlistInitialized,
    loading,
    
    // Actions
    refreshWatchlist,
    addCompanyToWatchlist,
    removeCompanyFromWatchlist,
    addIndustryToWatchlist,
    removeIndustryFromWatchlist,
  };
};